import React, { Fragment, useEffect, useState } from "react";
import { SNETButton, AlertBox, alertTypes, SNETTextfield } from "shared/dist/components";
import TechnicalInfo from "./TechnicalInfo";
import { REGION } from "../Routes";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import validator from "shared/dist/utils/validator";
import { submitOrganizationCostraints } from "../validationConstraints";
import ValidationError from "shared/dist/utils/validationError";
import { organizationActions } from "../../../Services/Redux/actionCreators";
import { organizationTypes } from "../../../Utils/organizationSetup";
import { checkIfKnownError } from "shared/dist/utils/error";
import { generateDetailedErrorMessageFromValidation } from "../../../Utils/validation";
import { storageProviders } from "../../../Utils/storageTypes";
import { isEmpty } from "lodash";
import { checkIsOwnerAddress } from "../../../Utils/Blockchain";
import { useFinishLater } from "..";
import { useNavigateTo } from "../../../GlobalRouter/hook/NavigateTo";
import FormContentCard from "../../../Components/PageFormComponents/FormContentCard";
import FormActions from "../../../Components/PageFormComponents/FormActions";

const PublishToBlockchain = () => {
    const navigateTo = useNavigateTo();
    const { handleFinishLater } = useFinishLater();
    const { organization, email, ownerEmail, ownerAddress } = useSelector(
        (state) => ({
            organization: state.organization,
            email: state.user.email,
            ownerEmail: state.organization.owner.username,
            ownerAddress: state.organization.owner.address,
        }),
        shallowEqual
    );

    const { name, type, uuid, storageProvider, lighthouseToken } = organization;
    const [alert, setAlert] = useState({});
    const [isSumbitDisabled, setIsSumbitDisabled] = useState(true);
    const dispatch = useDispatch();

    const handlePublish = async () => {
        setAlert({});
        try {
            const isNotValid = validator(organization, submitOrganizationCostraints);
            if (isNotValid) {
                const errorMessage = generateDetailedErrorMessageFromValidation(isNotValid);
                return setAlert({ type: alertTypes.ERROR, children: errorMessage });
            }
            if (email !== ownerEmail) {
                throw new ValidationError("Only owner can publish the organization");
            }
            await checkIsOwnerAddress(ownerAddress);
            await dispatch(organizationActions.submitForApproval(organization));
            const metadataUri = await dispatch(
                organizationActions.publishOrganization(uuid, storageProvider, lighthouseToken)
            );
            await dispatch(
                organizationActions.publishOrganizationInBlockchain(organization, metadataUri)
            );
            await dispatch(organizationActions.getOrganizations());
            await dispatch(organizationActions.selectOrganization(organization.uuid));
        } catch (error) {
            if (checkIfKnownError(error)) {
                return setAlert({ type: alertTypes.ERROR, message: error.message });
            }
            setAlert({ type: alertTypes.ERROR, message: "Unable to publish. Please try later" });
        } finally {
            navigateTo.services({ orgUuid: organization.uuid });
        }
    };

    const handleBack = () => {
        navigateTo.organizationSetup({ orgUuid: organization.uuid }, REGION.path);
    };

    useEffect(() => {
        const getIsFilecoinTokenExist = () => {
            const isFilecoinTokenExist =
                storageProvider !== storageProviders.FILECOIN || !isEmpty(lighthouseToken);

            if (!isFilecoinTokenExist) {
                setAlert({
                    type: alertTypes.ERROR,
                    message: "Please, fill the lighthouse token on Organization Profile tab!",
                });
            } else {
                setAlert({});
            }

            return isFilecoinTokenExist;
        };

        setIsSumbitDisabled(!ownerAddress || email !== ownerEmail || !getIsFilecoinTokenExist());
    }, [ownerAddress, email, ownerEmail, storageProvider, lighthouseToken]);

    return (
        <Fragment>
            <FormContentCard
                title="Publish Organization to Blockchain"
                description="Add your organisation to the blockchain, making sure you enter all relevant
                    information correctly, as once the data is submitted you will be unable to edit
                    it."
            >
                <SNETTextfield
                    label="Entity Type"
                    name="entity"
                    disabled
                    value={type}
                    list={[
                        {
                            value: organizationTypes.ORGANIZATION,
                            label: organizationTypes.ORGANIZATION,
                        },
                        {
                            value: organizationTypes.INDIVIDUAL,
                            label: organizationTypes.INDIVIDUAL,
                        },
                    ]}
                />
                <SNETTextfield
                    label="Company Organization Name"
                    description="The company name is displayed as the provider to users on the AI Marketplace page. "
                    name="name"
                    disabled
                    value={name}
                />
                <TechnicalInfo />
            </FormContentCard>
            <AlertBox message={alert.message} type={alert.type} children={alert.children} />
            <FormActions>
                <SNETButton color="primary" children="finish later" onClick={handleFinishLater} />
                <SNETButton color="primary" children="back" onClick={handleBack} />
                <SNETButton
                    color="primary"
                    variant="contained"
                    children="Publish to blockchain"
                    onClick={handlePublish}
                    disabled={isSumbitDisabled}
                />
            </FormActions>
        </Fragment>
    );
};

export default PublishToBlockchain;
